/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [COPY-REPEATS] ONE DUTY'S NAMES, COPIED ONTO THE SAME DUTY POINT'S OTHER DATES AT THE SAME
 *  TIME — THE WORDS THE SERVER AND THE FILL SCREEN SHARE.
 *
 *  ⚠️ A MODULE WITH NO IMPORTS, `fillState.constant.ts`'s reason: a client may read
 *  `@pams/shared/constants` at run time, and every other barrel drags zod in.
 *
 *  ⚠️ THE SKIP REASONS ARE A CLOSED SET THE SERVER DECIDES AND THE SCREEN WORDS. The fill screen's
 *  sentences are a `Record` over this tuple, so a reason added here does not compile there until it
 *  has words — "say what happened, and why for each reason" held by a type rather than a habit.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const COPY_REPEATS_SKIP_REASONS = [
  /** The duty has ended: `ends_at <= now`. */
  'OVER',
  /**
   * The duty has started (`starts_at <= now`), or somebody has already marked on it (ACTIVE). A
   * whole-day duty starts at 00:00 IST of its own date, so today's is STARTED all day — clearing a
   * name there takes marking authority off an officer who may be standing it.
   */
  'STARTED',
  /** The share was closed short with a reason. That is a decision, and a copy does not overwrite it. */
  'CLOSED_SHORT',
  /** More than half of the share is already named: somebody has done that work, and a copy replaces. */
  'MORE_THAN_HALF_NAMED',
  /** That date asks this share for a different force — a detached duty — or for none at all. */
  'DIFFERENT_FORCE',
  /**
   * An officer named on this duty cannot be named on that date: they hold another row there, they
   * may not stand a force post on that duty point, or they are no longer eligible. Nothing on that
   * date is written — a target is copied whole or not at all.
   */
  'NOT_NAMEABLE',
] as const;

export type CopyRepeatsSkipReason = (typeof COPY_REPEATS_SKIP_REASONS)[number];

/**
 * ⚠️ ONE DIVISION PER COPY, AND BOTH SIDES SAY IT IN THESE WORDS.
 *
 * A copy writes only the caller's own share, never another division's. On a sheet that shows
 * several divisions — a super admin or a CP-office nodal arriving without `?division=` — "own"
 * is undefined, so the server refuses with this sentence and the fill screen says it before the
 * click. One literal, `POLICY_AUTHOR_REFUSAL`'s precedent, so the two cannot drift.
 */
export const COPY_REPEATS_ONE_DIVISION_REFUSAL =
  'This duty shows more than one division. Open it from a division\u2019s row in Assign PA to copy that division\u2019s names.';
